import React, { useState, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import InlineEdit from '@atlaskit/inline-edit';
import { Box } from '@atlaskit/primitives';
import SmartUserPicker, { type Value, type OptionData } from '@atlaskit/smart-user-picker';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { useIntl } from '@atlassian/jira-intl';
import type { userField_servicedeskCustomerServiceCustomDetails$key } from '@atlassian/jira-relay/src/__generated__/userField_servicedeskCustomerServiceCustomDetails.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import commonMessages from '../messages';
import { InlineEditContainer } from '../styled';
import type { Field } from '../types';
import messages from './messages';
import { UserFieldReadView } from './read-view';

export type UserOption = OptionData | undefined | null;

export const UserField: FC<
	{ userFieldRef: userField_servicedeskCustomerServiceCustomDetails$key } & Pick<
		Field,
		'onChange' | 'getValueContainer'
	>
> = ({ userFieldRef, onChange, getValueContainer }) => {
	const data = useFragment(
		graphql`
			fragment userField_servicedeskCustomerServiceCustomDetails on CustomerServiceCustomDetailValue
			@argumentDefinitions(includePlatformValue: { type: "Boolean!" }) {
				id
				name
				platformValue @include(if: $includePlatformValue) {
					__typename
					... on CustomerServiceUserDetailValue {
						accountId
						name
						avatarUrl
					}
				}
				...readViewUserField_servicedeskCustomerServiceCustomDetails
					@arguments(includePlatformValue: $includePlatformValue)
			}
		`,
		userFieldRef || null,
	);

	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { id, name: label, platformValue } = data;
	const user: UserOption =
		platformValue?.__typename === 'CustomerServiceUserDetailValue' &&
		platformValue.accountId &&
		platformValue.name
			? {
					id: platformValue.accountId,
					name: platformValue.name,
					avatarUrl: platformValue.avatarUrl,
				}
			: undefined;
	const [selectedUser, setSelectedUser] = useState<UserOption>(user);
	const [isEditing, setIsEditing] = useState<boolean>(false);

	const onChangeWithAnalytics = (selectedOption: Value) => {
		// Value can be an array if isMulti is enabled, we're narrowing down the typing
		if (Array.isArray(selectedOption)) return;

		setSelectedUser(selectedOption);

		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'userFieldType',
		});

		onChange(
			{
				value: selectedOption?.id || '',
				...(selectedOption && {
					platformValue: {
						accountId: selectedOption.id,
						name: selectedOption.name,
						avatarUrl: selectedOption.avatarUrl,
					},
				}),
			},
			analyticsEvent,
		);
	};

	return (
		<InlineEditContainer>
			<InlineEdit
				defaultValue={undefined}
				editButtonLabel={formatMessage(commonMessages.editButtonLabel, {
					label,
					value: user?.name || formatMessage(commonMessages.noValue),
				})}
				editView={({ errorMessage, ...fieldProps }) => (
					<Box id={id}>
						<SmartUserPicker
							{...fieldProps}
							appearance="compact"
							styles={{
								control: (style) => ({
									...style,
									height: '32px',
								}),
							}}
							width="100%"
							value={selectedUser}
							fieldId="user-detail-field"
							includeUsers
							includeTeams={false}
							includeGroups={false}
							isClearable
							productKey="jira"
							debounceTime={400}
							siteId={cloudId}
							onChange={onChangeWithAnalytics}
							open
							placeholder={formatMessage(messages.searchPlaceholder)}
							onError={(e: Error) => {
								fireErrorAnalytics({
									meta: {
										id: 'userFieldSmartUserPicker',
										packageName: 'jiraServicedeskCustomerServiceCustomDetails',
										teamName: 'boysenberry',
									},
									sendToPrivacyUnsafeSplunk: true,
									error: e,
								});
							}}
						/>
					</Box>
				)}
				readView={() => (
					<UserFieldReadView
						userFieldRef={data}
						getValueContainer={getValueContainer}
						inInlineEdit
					/>
				)}
				readViewFitContainerWidth
				isEditing={isEditing}
				onEdit={() => setIsEditing(true)}
				hideActionButtons
				onConfirm={() => setIsEditing(false)}
			/>
		</InlineEditContainer>
	);
};

export { UserFieldReadView };
